// Client Portal 5-Day Follow-Up workflow. Team-driven: each day = a phone call
// outcome + a copy-ready SMS/Email (nothing auto-sends). Pauses on a promised
// date, stops on completion / not-proceeding. variant="compact" → list rows.
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Mail, MessageSquare, Copy, Check, CalendarClock, ShieldCheck, Ban, ChevronRight, Loader2, IdCard, Clock3 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useBranding } from '../../hooks/useBranding';

const API = process.env.REACT_APP_BACKEND_URL;
const NAVY = '#15133A';

const OUTCOMES = [
  { key: 'no_answer', label: 'No Answer' },
  { key: 'spoke', label: 'Spoke to Client' },
  { key: 'will_complete', label: 'Will Complete Portal' },
  { key: 'needs_help', label: 'Client Needs Help' },
  { key: 'no_longer_proceeding', label: 'No Longer Proceeding' },
];
const OUTCOME_LABEL = { discovery: 'Discovery call', no_answer: 'No answer', spoke: 'Spoke to client', will_complete: 'Will complete portal', needs_help: 'Needs help', no_longer_proceeding: 'Not proceeding' };
const fmtDay = (d) => { try { return new Date(`${d}T00:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }); } catch { return d; } };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Hobart' }).replace(/\s/g, ' '); } catch { return ''; } };

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// "Client said they'll do it Saturday" — pause the follow-up (calls + daily portal SMS) until a chosen day
function DelayFollowUp({ appId, headers, current, onSet, short }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async (date) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/onboarding/applications/${appId}/promised-date`, { date: date || null }, { headers });
      onSet?.(data); setOpen(false);
      toast.success(date ? `Follow-up delayed until ${fmtDay(date)}` : 'Follow-up resumed');
    } catch { toast.error('Could not delay follow-up'); } finally { setBusy(false); }
  };
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(); d.setDate(d.getDate() + i + 1); return d; });
  const twoWeeks = (() => { const d = new Date(); d.setDate(d.getDate() + 14); return d; })();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} data-testid={`fu-delay-${appId}`} title="Client will do it later — delay the next follow-up"
          className={`inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white font-bold text-indigo-700 transition-colors hover:border-indigo-400 hover:bg-indigo-50 whitespace-nowrap ${short ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock3 className="w-3.5 h-3.5" />}{current ? 'Change' : 'Delay'}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2" onClick={(e) => e.stopPropagation()} data-testid={`fu-delay-menu-${appId}`}>
        <p className="px-2 pt-1 pb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">Delay next follow-up until…</p>
        <div className="grid grid-cols-2 gap-1">
          {days.map((d, i) => (
            <button key={ymd(d)} type="button" disabled={busy} onClick={() => save(ymd(d))} data-testid={`fu-delay-opt-${i + 1}`}
              className={`rounded-lg px-2 py-1.5 text-left text-xs font-semibold hover:bg-indigo-50 ${current === ymd(d) ? 'bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200' : 'text-slate-700'}`}>
              {i === 0 ? 'Tomorrow' : d.toLocaleDateString('en-AU', { weekday: 'long' })}<span className="block text-[10px] font-medium text-slate-400">{d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
            </button>
          ))}
          <button type="button" disabled={busy} onClick={() => save(ymd(twoWeeks))} data-testid="fu-delay-two-weeks" className="rounded-lg px-2 py-1.5 text-left text-xs font-semibold text-slate-700 hover:bg-indigo-50">In 2 weeks<span className="block text-[10px] font-medium text-slate-400">{twoWeeks.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}</span></button>
        </div>
        <label className="mt-2 flex items-center gap-2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-600">
          <CalendarClock className="w-3.5 h-3.5 text-slate-400" /> Pick a date
          <input type="date" min={ymd(days[0])} disabled={busy} data-testid={`fu-delay-date-${appId}`} className="ml-auto bg-transparent text-xs font-semibold text-slate-800 outline-none" onChange={(e) => e.target.value && save(e.target.value)} />
        </label>
        {current && <button type="button" disabled={busy} onClick={() => save(null)} data-testid={`fu-delay-clear-${appId}`} className="mt-1.5 w-full rounded-lg px-2 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-50">Resume follow-up now</button>}
        <p className="px-2 pt-2 text-[10.5px] leading-snug text-slate-400">Pauses follow-up calls and the daily portal reminder texts until that day.</p>
      </PopoverContent>
    </Popover>
  );
}

function Meter({ count }) {
  return <span className="flex gap-1">{[0, 1, 2, 3, 4].map((i) => <span key={i} className={`h-1.5 w-4 rounded-full ${i < count ? 'bg-emerald-500' : 'bg-slate-200'}`} />)}</span>;
}

export function FollowUpStatus({ app, headers, variant = 'full', readOnly = false, short = false }) {
  const brand = useBranding();
  const appId = app.id;
  const [plan, setPlan] = useState(variant === 'compact' ? (app?.followup || null) : null);
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(null);
  const [localDone, setLocalDone] = useState(false);

  const loadPlan = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/onboarding/applications/${appId}/followup-plan`, { headers }); setPlan(data); }
    catch { /* ignore */ }
  }, [appId, headers]);

  useEffect(() => { if (variant === 'full') loadPlan(); }, [variant, loadPlan]);

  // ── Compact (dashboard list): one simple tap to tick off today's follow-up ──
  if (variant === 'compact') {
    if (!plan) return null;
    const cs = plan.contact_status;
    const TERMINAL = {
      paused: { txt: `Paused · ${fmtDay(plan.promised_complete_date)}`, cls: 'text-indigo-600' },
      completed: { txt: 'Portal completed', cls: 'text-emerald-600' },
      stopped: { txt: 'Follow-up stopped', cls: 'text-slate-400' },
      done: { txt: 'Follow-up complete', cls: 'text-slate-400' },
    };
    if (cs === 'paused') return (
      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <p data-testid={`fu-status-${appId}`} className={`text-xs font-black whitespace-nowrap ${TERMINAL.paused.cls}`}>{TERMINAL.paused.txt}</p>
        <DelayFollowUp appId={appId} headers={headers} current={plan.promised_complete_date} onSet={setPlan} short={short} />
      </div>
    );
    if (TERMINAL[cs]) return <p data-testid={`fu-status-${appId}`} className={`text-xs font-black ${TERMINAL[cs].cls}`} onClick={(e) => e.stopPropagation()}>{TERMINAL[cs].txt}</p>;

    const doneToday = cs === 'contacted' || localDone;
    const markToday = async (e) => {
      e.stopPropagation();
      if (busy === 'tick') return;
      setBusy('tick');
      try {
        await axios.post(`${API}/api/onboarding/applications/${appId}/followup-plan/${plan.current_day || 1}/phone`, { outcome: 'spoke' }, { headers });
        setLocalDone(true);
        toast.success('Follow-up ticked off for today');
      } catch { toast.error('Could not update follow-up'); } finally { setBusy(null); }
    };

    return (
      <div data-testid={`fu-compact-${appId}`} onClick={(e) => e.stopPropagation()}>
        {doneToday ? (
          <span data-testid={`fu-status-${appId}`} className="inline-flex items-center gap-1.5 text-xs font-black text-emerald-600 whitespace-nowrap"><Check className="w-4 h-4" /> {short ? 'Done today' : 'Followed up today'}</span>
        ) : (
          <button type="button" onClick={markToday} disabled={busy === 'tick'} data-testid={`fu-mark-today-${appId}`}
            className={`group inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-white text-xs font-bold text-rose-600 transition-colors hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-700 whitespace-nowrap ${short ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1'}`} title="Mark today's call follow-up as completed">
            {busy === 'tick' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="grid h-4 w-4 place-items-center rounded-full border-2 border-current"><Check className="h-2.5 w-2.5 opacity-0 transition-opacity group-hover:opacity-100" /></span>}
            {short ? 'Call due' : 'Call follow-up completed'}
          </button>
        )}
        {!doneToday && <span className="ml-1.5 inline-block align-middle"><DelayFollowUp appId={appId} headers={headers} onSet={setPlan} short={short} /></span>}
      </div>
    );
  }

  if (!plan) return <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading follow-up…</div>;

  const setPhone = async (day, outcome, extra = {}) => {
    setBusy(`phone-${day}`);
    try { const { data } = await axios.post(`${API}/api/onboarding/applications/${appId}/followup-plan/${day}/phone`, { outcome, ...extra }, { headers }); setPlan(data); }
    catch { toast.error('Could not record call'); } finally { setBusy(null); }
  };
  const markMessage = async (day, done) => {
    setBusy(`msg-${day}`);
    try { const { data } = await axios.post(`${API}/api/onboarding/applications/${appId}/followup-plan/${day}/message`, { done }, { headers }); setPlan(data); if (done) toast.success('Marked as sent'); }
    catch { toast.error('Could not update'); } finally { setBusy(null); }
  };
  const stop = async (reason) => {
    setBusy('stop');
    try { const { data } = await axios.post(`${API}/api/onboarding/applications/${appId}/followup-stop`, { reason }, { headers }); setPlan(data); toast.success(reason === null ? 'Follow-up resumed' : 'Follow-up sequence stopped'); }
    catch { toast.error('Could not update'); } finally { setBusy(null); }
  };
  const downloadVcf = async () => {
    try {
      const r = await axios.get(`${API}/api/onboarding/broker-contact.vcf`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = `${(brand.full_name || 'BROKER').toUpperCase()}.vcf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Could not download contact card'); }
  };
  const copyMsg = async (day, d) => {
    const text = d.channel === 'email' ? `Subject: ${d.subject}\n\n${d.template}` : d.template;
    try { await navigator.clipboard.writeText(text); setCopied(day); setTimeout(() => setCopied(null), 2000); toast.success(d.channel === 'email' ? 'Email copied' : 'SMS copied'); }
    catch { toast.error('Copy failed — select and copy manually'); }
  };

  // ── Completed / Stopped banners ──
  if (plan.completed) return <Banner testId={`fu-full-${appId}`} icon={ShieldCheck} tone="emerald" title="Client Portal Completed ✅" sub="Portal finished — all follow-ups stopped automatically." />;
  if (plan.stopped) return (
    <div data-testid={`fu-full-${appId}`} className="rounded-2xl border border-slate-300 bg-slate-50 p-4">
      <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-200 text-slate-600"><Ban className="w-5 h-5" /></span>
        <div className="flex-1"><p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Client Portal Follow-Up</p><p className="text-base font-extrabold text-slate-800">Follow-ups stopped</p><p className="text-xs text-slate-500">{plan.stopped.reason} · {plan.stopped.by}</p></div>
        <button type="button" onClick={() => stop(null)} disabled={busy === 'stop'} data-testid={`fu-resume-${appId}`} className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:border-slate-500">Resume</button>
      </div>
    </div>
  );

  const activeDay = plan.current_day;
  return (
    <div data-testid={`fu-full-${appId}`} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100" style={{ background: 'linear-gradient(90deg,#15133A,#241f52)' }}>
        <div><p className="text-[10px] font-bold uppercase tracking-widest text-indigo-200">Client Portal Follow-Up</p>
          <p className="text-sm font-extrabold text-white">{plan.paused ? `Paused until ${fmtDay(plan.promised_complete_date)}` : plan.state === 'exhausted' ? '5-day sequence complete' : `Day ${activeDay} of 5`}</p></div>
        <div className="flex flex-col items-end gap-1"><Meter count={plan.done_days} /><span className="text-[10px] font-bold text-indigo-200">{plan.done_days}/5 done</span></div>
      </div>

      {plan.inactivity && (() => {
        const I = plan.inactivity;
        const M = {
          not_required: { t: 'Not required', c: 'text-slate-400', d: 'bg-slate-300' },
          scheduled: { t: 'Scheduled', c: 'text-sky-700', d: 'bg-sky-500' },
          sent: { t: `Sent${I.at ? ` · ${fmtTime(I.at)}` : ''}${I.dry_run ? ' (dry-run)' : ''}`, c: 'text-emerald-700', d: 'bg-emerald-500' },
          cancelled: { t: 'Cancelled', c: 'text-slate-400', d: 'bg-slate-300' },
          suppressed: { t: `Suppressed until ${fmtDay(I.suppressed_until)}`, c: 'text-indigo-700', d: 'bg-indigo-500' },
        }[I.status] || { t: I.status, c: 'text-slate-400', d: 'bg-slate-300' };
        return (
          <div data-testid={`fu-inactivity-${appId}`} className="flex items-center gap-2 border-b border-slate-100 px-4 py-2">
            <span className={`h-1.5 w-1.5 rounded-full ${M.d}`} />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">45-min Inactivity Reminder</span>
            <span className={`text-[11px] font-black ${M.c}`}>{M.t}</span>
          </div>
        );
      })()}

      {plan.paused && (
        <div className="flex items-center justify-between gap-2 bg-indigo-50 border-b border-indigo-100 px-4 py-2.5">
          <p className="text-xs text-indigo-900 flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Client will complete by <b>{fmtDay(plan.promised_complete_date)}</b> — follow-ups paused.</p>
          <PromiseDate appId={appId} headers={headers} onSet={loadPlan} resume />
        </div>
      )}

      <div className="divide-y divide-slate-100">
        {[1, 2, 3, 4, 5].map((n) => {
          const d = plan.days[n];
          const isActive = n === activeDay && !plan.paused;
          const phoneOutcome = d.phone?.outcome;
          const showMsg = d.needs_message && (n === 1 || phoneOutcome === 'no_answer');
          return (
            <div key={n} data-testid={`fu-day-${n}-${appId}`} className={`px-4 py-3 ${isActive ? 'bg-amber-50/60' : d.complete ? 'bg-emerald-50/30' : ''}`}>
              <div className="flex items-center gap-2.5">
                <span className={`flex h-7 w-7 items-center justify-center rounded-lg text-[11px] font-black flex-shrink-0 ${d.complete ? 'bg-emerald-600 text-white' : isActive ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500'}`}>{d.complete ? <Check className="w-4 h-4" /> : n}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold" style={{ color: NAVY }}>Day {n}{isActive && <span className="ml-2 text-[10px] font-black uppercase tracking-wider text-amber-700">Active</span>}</p>
                  <p className="text-[11px] font-semibold text-slate-500 flex items-center gap-1.5 flex-wrap">
                    <Phone className="w-3 h-3" />{phoneOutcome ? <span className={phoneOutcome === 'no_answer' ? 'text-amber-700' : 'text-emerald-700'}>{OUTCOME_LABEL[phoneOutcome]}{d.phone?.at ? ` · ${fmtTime(d.phone.at)}` : ''}</span> : <span className="text-slate-400">Not attempted</span>}
                    {d.phone?.by && <span className="text-slate-400">· {d.phone.by}</span>}
                  </p>
                  {d.phone?.notes && <p className="text-[11px] text-slate-500 italic mt-0.5">“{d.phone.notes}”</p>}
                </div>
                {showMsg && (d.message?.done
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-black uppercase text-emerald-700"><Check className="w-3 h-3" />{d.channel === 'email' ? 'Email' : 'SMS'} sent</span>
                  : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-400">{d.channel === 'email' ? 'Email' : 'SMS'} outstanding</span>)}
              </div>

              {isActive && (
                <div className="mt-2.5 pl-10 space-y-2.5">
                  {/* Phone step */}
                  {!phoneOutcome && n > 1 && (
                    <div className="flex flex-wrap gap-1.5" data-testid={`fu-outcomes-${n}-${appId}`}>
                      {OUTCOMES.map((o) => (
                        <button key={o.key} type="button" disabled={busy === `phone-${n}`}
                          onClick={() => {
                            if (o.key === 'will_complete') { const dt = window.prompt('Client will complete by (YYYY-MM-DD):'); if (dt) setPhone(n, o.key, { promised_date: dt }); }
                            else if (o.key === 'no_longer_proceeding') { const r = window.prompt('Reason they are no longer proceeding:'); if (r !== null) setPhone(n, o.key, { notes: r }); }
                            else { const note = o.key === 'needs_help' ? window.prompt('What help do they need? (optional)') : null; setPhone(n, o.key, note ? { notes: note } : {}); }
                          }}
                          data-testid={`fu-outcome-${o.key}-${n}-${appId}`}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-bold text-slate-700 hover:border-indigo-500 hover:text-indigo-700">
                          {busy === `phone-${n}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Phone className="w-3 h-3" />} {o.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Message step (Copy + Mark sent) */}
                  {showMsg && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-2.5">
                      {d.channel === 'email' && <p className="text-[11px] font-bold text-slate-600 mb-1">Subject: {d.subject}</p>}
                      <p className="text-[11px] text-slate-600 whitespace-pre-wrap line-clamp-4 mb-2">{d.template}</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <button type="button" onClick={() => copyMsg(n, d)} data-testid={`fu-copy-${n}-${appId}`}
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white" style={{ backgroundColor: NAVY }}>
                          {copied === n ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied === n ? 'Copied' : d.channel === 'email' ? 'Copy Email' : 'Copy SMS'}
                        </button>
                        {d.has_vcf && <button type="button" onClick={downloadVcf} data-testid={`fu-vcf-${appId}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold hover:border-slate-500" style={{ color: NAVY }}><IdCard className="w-3.5 h-3.5" /> Contact card (.vcf)</button>}
                        {!d.message?.done
                          ? <button type="button" onClick={() => markMessage(n, true)} disabled={busy === `msg-${n}`} data-testid={`fu-mark-sent-${n}-${appId}`} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-white px-3 py-1.5 text-xs font-bold text-emerald-700 hover:border-emerald-500">{busy === `msg-${n}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Mark {d.channel === 'email' ? 'Email' : 'SMS'} Sent</button>
                          : <button type="button" onClick={() => markMessage(n, false)} className="text-xs font-bold text-slate-400 hover:text-slate-700">Undo</button>}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer: promise date + stop */}
      {!plan.paused && plan.state !== 'exhausted' && (
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-4 py-3">
          <PromiseDate appId={appId} headers={headers} onSet={loadPlan} />
          <button type="button" onClick={() => { const r = window.prompt('Stop follow-ups — short reason (e.g. purchased elsewhere, on hold):'); if (r) stop(r); }} disabled={busy === 'stop'} data-testid={`fu-stop-${appId}`} className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-white px-3 py-2 text-xs font-bold text-rose-600 hover:border-rose-400"><Ban className="w-3.5 h-3.5" /> Stop follow-ups</button>
        </div>
      )}
      {plan.state === 'exhausted' && <div className="border-t border-slate-100 px-4 py-3 text-xs font-semibold text-slate-500 flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> 5-day sequence complete — daily follow-ups stopped. <PromiseDate appId={appId} headers={headers} onSet={loadPlan} /></div>}
    </div>
  );
}

function Banner({ testId, icon: Icon, tone, title, sub }) {
  const t = tone === 'emerald' ? 'border-emerald-200 bg-emerald-50/60 text-emerald-900' : 'border-slate-300 bg-slate-50 text-slate-800';
  const ic = tone === 'emerald' ? 'bg-emerald-600' : 'bg-slate-400';
  return (
    <div data-testid={testId} className={`rounded-2xl border p-4 flex items-center gap-3 ${t}`}>
      <span className={`flex h-11 w-11 items-center justify-center rounded-2xl text-white ${ic}`}><Icon className="w-5 h-5" /></span>
      <div><p className="text-[10px] font-bold uppercase tracking-widest opacity-70">Client Portal Follow-Up</p><p className="text-base font-extrabold">{title}</p><p className="text-xs opacity-80">{sub}</p></div>
    </div>
  );
}

// One-tap date picker (transparent native input overlays the button)
function PromiseDate({ appId, headers, onSet, resume }) {
  const [busy, setBusy] = useState(false);
  const save = async (date) => {
    setBusy(true);
    try { await axios.post(`${API}/api/onboarding/applications/${appId}/promised-date`, { date: date || null }, { headers }); await onSet?.(); toast.success(date ? `Paused until ${fmtDay(date)}` : 'Resumed'); }
    catch { toast.error('Could not save date'); } finally { setBusy(false); }
  };
  if (resume) return <button type="button" onClick={() => save(null)} disabled={busy} data-testid={`fu-resume-${appId}`} className="rounded-full border border-indigo-300 bg-white px-3 py-1 text-xs font-bold text-indigo-700 hover:border-indigo-500 flex-shrink-0">Resume now</button>;
  return (
    <div className="relative inline-block">
      <span data-testid={`fu-set-date-${appId}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-bold select-none" style={{ color: NAVY }}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarClock className="w-3.5 h-3.5" />} Client promised to complete by…
      </span>
      <input type="date" min={new Date().toISOString().slice(0, 10)} disabled={busy} data-testid={`fu-date-input-${appId}`} aria-label="Client promised to complete by"
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        onClick={(e) => { e.stopPropagation(); try { e.currentTarget.showPicker?.(); } catch (_e) { /* noop */ } }}
        onChange={(e) => { e.stopPropagation(); if (e.target.value) save(e.target.value); }} />
    </div>
  );
}
